import * as XLSX from 'xlsx';
import { blobticService } from '../../Commun/services/blobticService';
import { BlobticDataManager } from '../../Commun/services/blobticDataManager';
import { academyApi } from './academyApi';
import { academyStore } from './academyStore';

export interface EntityField {
  fieldName: string;
  key: string;
  headerLabel: string;
  label: string;
  dataType: string;
  isRequired: boolean;
  sampleValue?: any;
  description?: string;
  maxLength?: number;
  isPrimaryKey?: boolean;
}

export interface EntitySchema {
  entityKey: string;
  displayName: string;
  description: string;
  fields: EntityField[];
  totalCount?: number;
  recordCount?: number;
}

export interface ImportRowError {
  rowIndex: number;
  code: string;
  errorMessage: string;
}

export interface ImportResult {
  entityKey: string;
  totalProcessed: number;
  totalImported: number;
  totalUpdated: number;
  totalErrors: number;
  errors: ImportRowError[];
}

export interface ExportResult {
  entityKey: string;
  displayName: string;
  fields: EntityField[];
  totalCount: number;
  rows: Record<string, any>[];
}

/**
 * Normalise un champ d'entité quelle que soit la casse ou la convention du backend
 */
export const normalizeField = (f: any): EntityField => {
  const key = String(f?.key || f?.Key || f?.fieldName || f?.FieldName || '');
  const label = String(f?.label || f?.Label || f?.headerLabel || f?.HeaderLabel || key);
  return {
    key,
    fieldName: key,
    label,
    headerLabel: label,
    dataType: String(f?.dataType || f?.DataType || 'string').toLowerCase(),
    isRequired: Boolean(f?.isRequired ?? f?.IsRequired ?? false),
    sampleValue: f?.sampleValue !== undefined ? f.sampleValue : (f?.SampleValue !== undefined ? f.SampleValue : ''),
    description: String(f?.description || f?.Description || ''),
    maxLength: Number(f?.maxLength ?? f?.MaxLength ?? 250),
  };
};

/**
 * Normalise un schéma d'entité et assure la cohérence des propriétés
 */
export const normalizeSchema = (s: any): EntitySchema => {
  const rawFields = Array.isArray(s?.fields) ? s.fields : (Array.isArray(s?.Fields) ? s.Fields : []);
  const count = Number(s?.totalCount ?? s?.TotalCount ?? s?.recordCount ?? s?.RecordCount ?? 0);
  return {
    entityKey: String(s?.entityKey || s?.EntityKey || ''),
    displayName: String(s?.displayName || s?.DisplayName || ''),
    description: String(s?.description || s?.Description || ''),
    totalCount: count,
    recordCount: count,
    fields: rawFields.map(normalizeField),
  };
};

/**
 * Mapping des clés d'entités d'import/export vers le nom de l'object store IndexedDB correspondant
 */
export const ENTITY_TO_STORE_NAME: Record<string, string> = {
  rooms: 'academy_room',
  teachers: 'academy_teacher',
  students: 'academy_student',
  groups: 'academy_group',
  courses: 'academy_course',
};

/**
 * Synchronise et invalide le cache IndexedDB et les stores en mémoire pour une entité après import
 */
export async function syncEntityIndexedDb(entityKey: string): Promise<void> {
  const storeName = ENTITY_TO_STORE_NAME[entityKey];
  if (!storeName) return;

  try {
    // 1. Vider immédiatement l'object store IndexedDB et le cache mémoire local
    if (BlobticDataManager.isValidContext()) {
      await BlobticDataManager.removeStores([storeName]);
    }

    // 2. Notifier academyStore pour invalider son snapshot
    academyStore.notify([storeName]);

    // 3. Recharger en arrière-plan sans bloquer l'interface ni le dialogue de résultat
    setTimeout(async () => {
      try {
        switch (entityKey) {
          case 'rooms':
            await academyApi.getRooms();
            break;
          case 'teachers':
            await academyApi.getTeachers();
            break;
          case 'students':
            await academyApi.getStudents();
            break;
          case 'groups':
            await academyApi.getGroups();
            break;
          case 'courses':
            await academyApi.getCourses();
            break;
          default:
            break;
        }
        await academyStore.refreshDashboardStats(true);
      } catch (bgErr) {
        console.warn(`Background re-fetch error for ${entityKey}:`, bgErr);
      }
    }, 100);
  } catch (err) {
    console.error(`Erreur lors de la synchronisation IndexedDB pour ${entityKey}:`, err);
  }
}

class ImportExportApiService {
  /**
   * Récupère la liste des schémas d'entités avec les compteurs actuels
   */
  async getSchemas(): Promise<EntitySchema[]> {
    const res = await blobticService.executeZoomRequest<any[]>({
      ZoomKey: 'ZOOM_IMPORT_EXPORT_SCHEMAS',
      Action: {
        OKey: 'Import_Export_Data,academy_schema,get_schemas',
      },
      showLoader: false,
    });
    const rawList = Array.isArray(res.data) ? res.data : [];
    return rawList.map(normalizeSchema);
  }

  /**
   * Récupère le modèle / schéma pour une entité spécifique
   */
  async getTemplate(entityKey: string): Promise<EntitySchema> {
    const res = await blobticService.executeZoomRequest<any>({
      ZoomKey: 'ZOOM_IMPORT_EXPORT_TEMPLATE',
      Action: {
        OKey: 'Import_Export_Data,academy_template,get_template',
        ENTITY_KEY: entityKey,
      },
      showLoader: false,
    });
    const payload = Array.isArray(res.data) ? res.data[0] : res.data;
    return normalizeSchema(payload);
  }

  /**
   * Importe un ensemble de lignes vers la base de données
   */
  async importData(
    entityKey: string,
    rows: Record<string, any>[],
    updateExisting: boolean
  ): Promise<ImportResult> {
    const jsonRows = JSON.stringify(rows);
    const res = await blobticService.executeZoomRequest<any>({
      ZoomKey: 'ZOOM_IMPORT_EXPORT_IMPORT',
      Action: {
        OKey: 'Import_Export_Data,academy_import,import_data',
        ENTITY_KEY: entityKey,
        UPDATE_EXISTING: updateExisting ? 1 : 0,
        ROWS: jsonRows,
        rows_json: jsonRows,
      },
      showLoader: true,
    });

    const payload = Array.isArray(res.data) ? res.data[0] : res.data;
    const d = payload || {};
    const rawErrors = Array.isArray(d.errors || d.Errors) ? (d.errors || d.Errors) : [];
    const result: ImportResult = {
      entityKey: d.entityKey || entityKey,
      totalProcessed: Number(d.totalProcessed ?? d.totalRows ?? d.TotalRows ?? rows.length),
      totalImported: Number(d.totalImported ?? d.insertedCount ?? d.InsertedCount ?? 0),
      totalUpdated: Number(d.totalUpdated ?? d.updatedCount ?? d.UpdatedCount ?? 0),
      totalErrors: Number(d.totalErrors ?? d.errorCount ?? d.ErrorCount ?? rawErrors.length),
      errors: rawErrors.map((e: any) => ({
        rowIndex: Number(e.rowIndex ?? e.RowIndex ?? 0),
        code: String(e.code ?? e.Code ?? ''),
        errorMessage: String(e.message ?? e.errorMessage ?? e.Message ?? ''),
      })),
    };

    // Si des lignes ont été insérées ou mises à jour, synchroniser et invalider immédiatement IndexedDB
    if (result.totalImported > 0 || result.totalUpdated > 0) {
      await syncEntityIndexedDb(entityKey);
    }

    return result;
  }

  /**
   * Exporte les données d'une entité pour l'agence connectée
   */
  async exportData(entityKey: string): Promise<ExportResult> {
    const res = await blobticService.executeZoomRequest<any>({
      ZoomKey: 'ZOOM_IMPORT_EXPORT_EXPORT',
      Action: {
        OKey: 'Import_Export_Data,academy_export,export_data',
        ENTITY_KEY: entityKey,
      },
      showLoader: true,
    });

    const payload = Array.isArray(res.data) ? res.data[0] : res.data;
    const data = payload || {};
    const rawFields = Array.isArray(data.fields) ? data.fields : (Array.isArray(data.Fields) ? data.Fields : []);
    const rows = Array.isArray(data.rows) ? data.rows : [];
    const count = Number(data.totalCount ?? data.recordCount ?? rows.length);

    return {
      entityKey: data.entityKey || entityKey,
      displayName: data.displayName || '',
      fields: rawFields.map(normalizeField),
      totalCount: count,
      rows,
    };
  }

  /**
   * Télécharge un modèle Excel (.xlsx) prérempli avec un exemple
   */
  downloadTemplateExcel(schema: EntitySchema, titleOverride?: string): void {
    const headers = schema.fields.map((f) => f.headerLabel || f.fieldName || f.key || 'Field');
    const sampleRow = schema.fields.map((f) => String(f.sampleValue ?? ''));

    const wsData = [headers, sampleRow];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Définir des largeurs de colonnes esthétiques
    ws['!cols'] = schema.fields.map((f) => ({
      wch: Math.max(
        String(f.headerLabel || f.fieldName || '').length + 4,
        String(f.sampleValue || '').length + 4,
        15
      ),
    }));

    const wb = XLSX.utils.book_new();
    const sheetName = (titleOverride || schema.displayName || schema.entityKey).slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    XLSX.writeFile(wb, `Modele_${schema.entityKey}.xlsx`);
  }

  /**
   * Télécharge un modèle CSV (.csv UTF-8 avec BOM) prérempli avec un exemple
   */
  downloadTemplateCsv(schema: EntitySchema): void {
    const headers = schema.fields
      .map((f) => `"${String(f.headerLabel || f.fieldName || f.key || '').replace(/"/g, '""')}"`)
      .join(';');
    const sampleRow = schema.fields
      .map((f) => `"${String(f.sampleValue ?? '').replace(/"/g, '""')}"`)
      .join(';');

    const csvContent = '\uFEFF' + headers + '\r\n' + sampleRow + '\r\n';
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `Modele_${schema.entityKey}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Télécharge les données exportées en Excel ou CSV
   */
  downloadExportFile(result: ExportResult, format: 'xlsx' | 'csv', titleOverride?: string): void {
    const dateStr = new Date().toISOString().slice(0, 10);
    const filename = `Export_${result.entityKey}_${dateStr}`;

    // Mapper les champs techniques vers les en-têtes conviviaux
    const formattedRows = result.rows.map((row) => {
      const formatted: Record<string, any> = {};
      for (const f of result.fields) {
        const label = f.headerLabel || f.fieldName || f.key;
        const key = f.fieldName || f.key;
        formatted[label] = row[key] ?? row[label] ?? '';
      }
      return formatted;
    });

    const ws = XLSX.utils.json_to_sheet(formattedRows);
    ws['!cols'] = result.fields.map((f) => ({
      wch: Math.max(String(f.headerLabel || f.fieldName || '').length + 4, 16),
    }));

    const wb = XLSX.utils.book_new();
    const sheetName = (titleOverride || result.displayName || result.entityKey).slice(0, 31);
    XLSX.utils.book_append_sheet(wb, ws, sheetName);

    if (format === 'xlsx') {
      XLSX.writeFile(wb, `${filename}.xlsx`);
    } else {
      const csvOutput = XLSX.utils.sheet_to_csv(ws, { FS: ';' });
      const blob = new Blob(['\uFEFF' + csvOutput], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', `${filename}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  }

  /**
   * Analyse un fichier Excel ou CSV déposé et normalise ses lignes selon le schéma
   */
  async parseUploadedFile(
    file: File,
    schema: EntitySchema
  ): Promise<{ rows: Record<string, any>[]; rawHeaders: string[] }> {
    const arrayBuffer = await file.arrayBuffer();
    const wb = XLSX.read(arrayBuffer, { type: 'array' });
    const firstSheetName = wb.SheetNames[0];
    const ws = wb.Sheets[firstSheetName];

    // Lire en tant que tableau de tableaux (Ligne 0 = headers)
    const rawData: any[][] = XLSX.utils.sheet_to_json(ws, { header: 1, defval: '' });
    if (!rawData || rawData.length === 0) {
      return { rows: [], rawHeaders: [] };
    }

    const rawHeaders: string[] = (rawData[0] || []).map((h: any) => String(h || '').trim());
    const dataRows = rawData
      .slice(1)
      .filter((r) => r.some((cell) => cell !== '' && cell !== null && cell !== undefined));

    // Construction d'une table de correspondance d'index
    const headerMapping: { colIndex: number; field: EntityField }[] = [];

    schema.fields.forEach((field) => {
      const foundIdx = rawHeaders.findIndex((h) => {
        const cleanH = h.toLowerCase().replace(/[\s_\-]/g, '');
        const cleanLabel = String(field.headerLabel || field.label || '').toLowerCase().replace(/[\s_\-]/g, '');
        const cleanName = String(field.fieldName || field.key || '').toLowerCase().replace(/[\s_\-]/g, '');
        return cleanH === cleanLabel || cleanH === cleanName;
      });

      if (foundIdx !== -1) {
        headerMapping.push({ colIndex: foundIdx, field });
      }
    });

    const normalizedRows: Record<string, any>[] = dataRows.map((r) => {
      const rowObj: Record<string, any> = {};
      headerMapping.forEach(({ colIndex, field }) => {
        let cellVal = r[colIndex];
        if (cellVal === undefined || cellVal === null) cellVal = '';

        const type = String(field.dataType || '').toLowerCase();
        if (type === 'number' || type === 'int' || type === 'decimal') {
          const num = Number(cellVal);
          rowObj[field.fieldName] = isNaN(num) ? 0 : num;
        } else if (type === 'date') {
          if (cellVal instanceof Date) {
            rowObj[field.fieldName] = cellVal.toISOString().slice(0, 10);
          } else {
            rowObj[field.fieldName] = String(cellVal).trim();
          }
        } else {
          rowObj[field.fieldName] = String(cellVal).trim();
        }
      });
      return rowObj;
    });

    return { rows: normalizedRows, rawHeaders };
  }
}

export const importExportApi = new ImportExportApiService();
